import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface JobFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isVerifiedAlumni: boolean;
  jobCommunityOnly: boolean;
  onCommunityOnlyChange: (v: boolean) => void;
  jobReferralOnly: boolean;
  onReferralOnlyChange: (v: boolean) => void;
  jobExternalOnly: boolean;
  onExternalOnlyChange: (v: boolean) => void;
  jobWorkModes: string[];
  onWorkModeToggle: (mode: string) => void;
  jobLocation: string;
  onLocationChange: (v: string) => void;
  jobIndustryTags: string[];
  onIndustryTagToggle: (tag: string) => void;
  activeJobTags?: { industries: string[] };
  onClearAll: () => void;
}

function FilterPill({
  active,
  onClick,
  activeClassName,
  children,
}: {
  active: boolean;
  onClick: () => void;
  activeClassName: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-8 px-3 rounded-full text-xs font-semibold border transition-colors font-sans",
        active
          ? activeClassName
          : "bg-transparent border-white/10 text-zinc-500 hover:border-white/20 hover:text-zinc-300",
      )}
    >
      {children}
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 font-sans">
      {children}
    </p>
  );
}

export function JobFilterDrawer({
  open,
  onOpenChange,
  isVerifiedAlumni,
  jobCommunityOnly,
  onCommunityOnlyChange,
  jobReferralOnly,
  onReferralOnlyChange,
  jobExternalOnly,
  onExternalOnlyChange,
  jobWorkModes,
  onWorkModeToggle,
  jobLocation,
  onLocationChange,
  jobIndustryTags,
  onIndustryTagToggle,
  activeJobTags,
  onClearAll,
}: JobFilterDrawerProps) {
  const hasActive =
    (isVerifiedAlumni && (jobCommunityOnly || jobReferralOnly || jobExternalOnly)) ||
    jobWorkModes.length > 0 ||
    jobLocation.trim() !== "" ||
    jobIndustryTags.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col w-80 max-w-[88vw] bg-zinc-950 border-l border-white/10 p-0 gap-0"
      >
        <SheetHeader className="flex-none px-6 py-5 border-b border-white/10">
          <SheetTitle
            className="text-sm font-bold uppercase tracking-widest text-white"
            style={{ fontFamily: "var(--font-space-grotesk, 'Space Grotesk', sans-serif)" }}
          >
            Filter Opportunities
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
          {isVerifiedAlumni && (
            <div>
              <SectionLabel>Post Type</SectionLabel>
              <div className="flex flex-wrap gap-2">
                <FilterPill
                  active={jobCommunityOnly}
                  onClick={() => onCommunityOnlyChange(!jobCommunityOnly)}
                  activeClassName="bg-cyan-500/20 border-cyan-500/40 text-cyan-300"
                >
                  Community Posts
                </FilterPill>
                <FilterPill
                  active={jobReferralOnly}
                  onClick={() => onReferralOnlyChange(!jobReferralOnly)}
                  activeClassName="bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                >
                  Referrals
                </FilterPill>
                <FilterPill
                  active={jobExternalOnly}
                  onClick={() => onExternalOnlyChange(!jobExternalOnly)}
                  activeClassName="bg-primary/20 border-primary/40 text-primary"
                >
                  External Posts
                </FilterPill>
              </div>
            </div>
          )}

          <div>
            <SectionLabel>Work Mode</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {(["remote", "hybrid", "on-site"] as const).map((mode) => {
                const labels: Record<string, string> = {
                  remote: "Remote",
                  hybrid: "Hybrid",
                  "on-site": "On-site",
                };
                return (
                  <FilterPill
                    key={mode}
                    active={jobWorkModes.includes(mode)}
                    onClick={() => onWorkModeToggle(mode)}
                    activeClassName="bg-zinc-200 text-zinc-900 border-transparent"
                  >
                    {labels[mode]}
                  </FilterPill>
                );
              })}
            </div>
          </div>

          <div>
            <SectionLabel>Location</SectionLabel>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
              <Input
                placeholder="City, country..."
                className="pl-9 bg-zinc-900/50 border-white/10 focus-visible:ring-primary/50 text-white font-sans"
                value={jobLocation}
                onChange={(e) => onLocationChange(e.target.value)}
              />
            </div>
          </div>

          {activeJobTags && activeJobTags.industries.length > 0 && (
            <div>
              <SectionLabel>Industry</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {activeJobTags.industries.map((tag) => (
                  <FilterPill
                    key={tag}
                    active={jobIndustryTags.includes(tag)}
                    onClick={() => onIndustryTagToggle(tag)}
                    activeClassName="bg-primary/20 border-primary/40 text-primary"
                  >
                    {tag}
                  </FilterPill>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex-none border-t border-white/10 px-6 py-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClearAll}
            disabled={!hasActive}
            className="text-xs font-semibold font-sans text-zinc-500 hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none"
          >
            Clear all
          </button>
          <SheetClose asChild>
            <button
              type="button"
              className="h-9 px-5 rounded-md bg-primary text-white text-xs font-bold uppercase tracking-wide hover:bg-primary/90 transition-colors font-sans"
            >
              Done
            </button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}
